import React, { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import "../styles/NammaPass.css";


import phoneMockup from "../Images/Namma/3.webp";
import metropassImg from "../Images/Namma/metropass.png";
import rekerBg from "../Images/Namma/reker_new.png";
import image120 from "../Images/Namma/120.jpg";
import image121 from "../Images/Namma/121.jpg";
import image122 from "../Images/Namma/122.jpg";
import image123 from "../Images/Namma/123.jpg";
import image124 from "../Images/Namma/124.jpg";
import image125 from "../Images/Namma/125.jpg";
import image126 from "../Images/Namma/126.webp";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };

/* ── Fixed full-site background image ── */
const SiteBg: React.FC = () => (
  <div style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }}>
    <img
      src={rekerBg}
      alt=""
      style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", display: "block" }}
    />
    {/* dark overlay — keeps all text readable */}
    <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.78)" }} />
    {/* subtle green tint */}
    <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse 80% 60% at 55% 20%, rgba(140,255,46,0.05) 0%, transparent 65%)" }} />
  </div>
);


const IconRewards = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <circle cx="20" cy="20" r="16" stroke="currentColor" strokeWidth="2"/>
    <path d="M20 10v2M20 28v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M15 24c0 2.21 2.24 4 5 4s5-1.79 5-4-2.24-4-5-4-5-1.79-5-4 2.24-4 5-4 5 1.79 5 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const IconDigital = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <rect x="4" y="10" width="32" height="20" rx="4" stroke="currentColor" strokeWidth="2"/>
    <path d="M4 17h32" stroke="currentColor" strokeWidth="2"/>
    <rect x="8" y="22" width="10" height="4" rx="2" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M28 22c1.5 0 3 .9 3 2s-1.5 2-3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    <path d="M25 22c1.5 0 3 .9 3 2s-1.5 2-3 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const IconNFC = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <rect x="6" y="8" width="28" height="24" rx="4" stroke="currentColor" strokeWidth="2"/>
    <path d="M14 20h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M14 15h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M14 25h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <circle cx="28" cy="25" r="2.5" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M26.5 23.5l3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const IconSecure = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
    <rect x="10" y="18" width="20" height="16" rx="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M14 18v-4a6 6 0 0112 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="20" cy="26" r="2.5" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M20 28.5v2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const FEATURES = [
  { label: "Unlimited Rewards", sub: "Amazing Value",        Icon: IconRewards, color: "#8cff2e", bg: "rgba(140,255,46,0.12)",  border: "rgba(140,255,46,0.28)" },
  { label: "100% Digital",      sub: "No Physical Cards",    Icon: IconDigital, color: "#8cff2e", bg: "rgba(140,255,46,0.12)",  border: "rgba(140,255,46,0.28)" },
  { label: "NFC",               sub: "Tap & Go",             Icon: IconNFC,     color: "#8cff2e", bg: "rgba(140,255,46,0.12)",  border: "rgba(140,255,46,0.28)" },
  { label: "Super Secure",      sub: "Blockchain Encrypted", Icon: IconSecure,  color: "#8cff2e", bg: "rgba(140,255,46,0.12)",  border: "rgba(140,255,46,0.28)" },
];

const IMPACT_CARDS = [
  { title: "Revolutionary", sub: "CO2 Offset Tech", accent: "#8cff2e" },
  { title: "Zero",          sub: "Hassle",          accent: "#8cff2e" },
  { title: "Seamless",      sub: "Experience",      accent: "#8cff2e" },
];

const COL_IMAGES = [
  [image120, image123, image126],
  [image121, image124],
  [image122, image125],
];

const NammaPass: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 55]);

  // Measure each column's half-height for seamless pixel-based loop
  const colRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [colHeights, setColHeights] = useState<number[]>([0, 0, 0]);

  useEffect(() => {
    const measure = () => {
      setColHeights(
        colRefs.current.map(el => (el ? el.scrollHeight / 2 : 0))
      );
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  return (
    <div className="np-root">
      <SiteBg />

      {/* ══════════════════════════════ HERO ══════════════════════════════ */}
      <section className="np-hero" ref={heroRef}>

        <div className="np-hero-inner">
          {/* left copy */}
          <motion.div variants={stagger} initial="hidden" animate="show">

            <motion.div variants={fadeUp} className="np-badge">
              <span className="np-badge-pill">✦ upto 20% Cashback</span>
              <span className="np-badge-text">Save Big on Every Ride</span>
            </motion.div>

            <motion.h1 variants={fadeUp} className="np-headline">
              Daily Metro?<br />
              <span className="np-headline-accent">Daily Reward-o!</span>
            </motion.h1>

            <motion.p variants={fadeUp} className="np-tagline">
              <span className="np-tagline-green">Smart.</span>{" "}
              Fast.{" "}
              <span className="np-tagline-green">Effortless.</span>
            </motion.p>

            <motion.ul variants={fadeUp} className="np-check-list">
              {[
                "No physical cards.",
                "No recharge counters.",
                "Just Tap & Ride with Reker.",
              ].map(t => (
                <li key={t} className="np-check-item">
                  <span className="np-check-icon">✓</span>
                  <span className="np-check-text">{t}</span>
                </li>
              ))}
            </motion.ul>

            <motion.div variants={fadeUp} className="np-kannada">
              <div className="np-kannada-title">ಹಸಿರು ಬೆಂಗಳೂರು</div>
              <div className="np-kannada-sub">Move Smart. Travel Green.</div>
            </motion.div>

            <motion.div variants={fadeUp} className="np-store-btns">
              <a
                href="https://play.google.com/store/apps/details?id=reker.example.evoltech&pcampaignid=web_share"
                target="_blank" rel="noopener noreferrer"
                className="np-store-btn"
                aria-label="Get it on Google Play"
              >
                <svg className="np-store-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.18 1.07C2.76 1.3 2.5 1.76 2.5 2.37v19.26c0 .61.26 1.07.68 1.3l.1.06 10.79-10.79v-.25L3.28 1.01l-.1.06z" fill="url(#gp-a)"/>
                  <path d="M17.57 15.4l-3.6-3.6v-.26l3.6-3.6.08.05 4.27 2.43c1.22.69 1.22 1.82 0 2.51l-4.27 2.43-.08.04z" fill="url(#gp-b)"/>
                  <path d="M17.65 15.36L13.97 11.67 3.18 22.46c.4.43 1.06.48 1.8.05l12.67-7.15" fill="url(#gp-c)"/>
                  <path d="M17.65 8.64L4.98 1.49C4.24 1.06 3.58 1.11 3.18 1.54l10.79 10.13 3.68-3.03z" fill="url(#gp-d)"/>
                  <defs>
                    <linearGradient id="gp-a" x1="13.17" y1="2.1" x2="-1.17" y2="16.44" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#00A0FF"/><stop offset="1" stopColor="#00BEFF"/>
                    </linearGradient>
                    <linearGradient id="gp-b" x1="22.5" y1="11.67" x2="2.18" y2="11.67" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#FFD000"/><stop offset="1" stopColor="#FFBC00"/>
                    </linearGradient>
                    <linearGradient id="gp-c" x1="15.17" y1="13.37" x2="-3.5" y2="32.04" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#FF3A44"/><stop offset="1" stopColor="#C31162"/>
                    </linearGradient>
                    <linearGradient id="gp-d" x1="1.07" y1="-3.8" x2="9.47" y2="4.6" gradientUnits="userSpaceOnUse">
                      <stop stopColor="#32A071"/><stop offset="1" stopColor="#2DA771"/>
                    </linearGradient>
                  </defs>
                </svg>
                <div className="np-store-text">
                  <span className="np-store-label">GET IT ON</span>
                  <span className="np-store-name">Google Play</span>
                </div>
              </a>

              <a
                href="https://apps.apple.com/in/app/reker/id6754635302"
                target="_blank" rel="noopener noreferrer"
                className="np-store-btn"
                aria-label="Download on the App Store"
              >
                <svg className="np-store-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="np-store-text">
                  <span className="np-store-label">DOWNLOAD ON THE</span>
                  <span className="np-store-name">App Store</span>
                </div>
              </a>
            </motion.div>

          </motion.div>

          {/* right phone */}
          <div className="np-hero-right">
            <motion.div
              className="np-phone-wrap"
              style={{ y: phoneY }}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="np-phone-glow" />
              <div className="np-phone-halo" />
              <div className="np-phone-body animate-float">
                <div className="np-phone-notch" />
                <div className="np-phone-screen">
                  <img
                    src={phoneMockup}
                    alt="NammaPass App"
                  fetchPriority="high"
                    decoding="async"
                  />
                </div>
                <div className="np-phone-shine" />
              </div>
            </motion.div>
          </div>
        </div>

      </section>

      {/* ══════════════════════════════ WHAT IS NAMMA PASS ══════════════════════════════ */}
      <section className="np-section np-what-bg" id="what">

        <div className="np-section-inner">

          <motion.div className="np-what-header"
            variants={stagger} initial="hidden" whileInView="show"
            viewport={{ once: true, margin: "-80px" }}>
            <motion.h2 variants={fadeUp} className="np-section-title">
              What is Namma Pass?
            </motion.h2>
            <motion.p variants={fadeUp} className="np-section-sub">
              Namma Pass is Bengaluru's all-digital namma metro membership inside the Reker app.
            </motion.p>
            <div className="np-divider" />
          </motion.div>

          {/* feature cards */}
          <motion.div className="np-cards-grid"
            variants={stagger} initial="hidden" whileInView="show"
            viewport={{ once: true, margin: "-60px" }}>
            {FEATURES.map(f => (
              <motion.div key={f.label} variants={fadeUp} className="np-feat-card"
                style={{ "--card-color": f.color, "--card-bg": f.bg, "--card-border": f.border } as React.CSSProperties}>
                <div className="np-feat-icon-wrap">
                  <f.Icon />
                </div>
                <div className="np-feat-title">{f.label}</div>
                <div className="np-feat-sub">{f.sub}</div>
              </motion.div>
            ))}
          </motion.div>

          {/* metro pass image */}
          <motion.div className="np-pass-wrap"
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }} viewport={{ once: true }}>
            <div className="np-pass-glow" />
            <img src={metropassImg} alt="Namma Pass Metro Pass" />
            <div className="np-pass-overlay" />
          </motion.div>

          {/* revolutionizing */}
          <motion.div className="np-impact-block"
            variants={stagger} initial="hidden" whileInView="show"
            viewport={{ once: true, margin: "-60px" }}>
            <motion.h3 variants={fadeUp} className="np-impact-title">
              Revolutionizing Daily Commute
            </motion.h3>
            <motion.div variants={fadeUp} className="np-impact-texts">
              <p><strong>Eradicating</strong> the hassle of physical cards, long queues, and recharge counters at BMRCL.</p>
              <p><strong>Transforming</strong> your namma metro experience with QR digital ticketing.</p>
              <p><strong>Eliminating</strong> friction from your daily travel, one tap at a time.</p>
            </motion.div>
            <motion.div variants={fadeUp} className="np-impact-cards">
              {IMPACT_CARDS.map(c => (
                <div key={c.title} className="np-impact-card">
                  <div className="np-impact-card-title" style={{ color: c.accent }}>{c.title}</div>
                  <div className="np-impact-card-sub">{c.sub}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>

        </div>

      </section>

      {/* ══════════════════════════════ APP EXPERIENCE ══════════════════════════════ */}
      <section className="np-section np-exp-bg" id="experience">

        <div className="np-section-inner np-exp-header">
          <motion.div variants={stagger} initial="hidden" whileInView="show"
            viewport={{ once: true, margin: "-60px" }}>
            <motion.div variants={fadeUp} className="np-label">App Experience</motion.div>
            <motion.h2 variants={fadeUp} className="np-section-title np-center">
              Experience Namma Pass
            </motion.h2>
            <motion.p variants={fadeUp} className="np-section-sub np-center">
              Seamless namma metro travel at your fingertips. Explore every feature.
            </motion.p>
          </motion.div>
        </div>

        <div className="np-masonry-grid">
          {COL_IMAGES.map((col, ci) => (
            <div key={ci} className="np-masonry-col">
              <motion.div
                ref={(el) => { colRefs.current[ci] = el; }}
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
                animate={colHeights[ci] > 0
                  ? { y: ci % 2 === 0 ? [0, -colHeights[ci]] : [-colHeights[ci], 0] }
                  : false
                }
                transition={{ y: { repeat: Infinity, repeatType: "loop", duration: 36 + ci * 8, ease: "linear" } }}
              >
                {[...col, ...col, ...col, ...col].map((img, idx) => (
                  <div key={idx} className="np-masonry-phone">
                    <div className="np-iphone-frame">
                      <div className="np-iphone-btn-vol-up" />
                      <div className="np-iphone-btn-vol-dn" />
                      <div className="np-iphone-btn-power" />
                      <div className="np-iphone-screen">
                        <div className="np-iphone-island" />
                        <img src={img} alt={`App screen ${idx + 1}`} loading="lazy"
                          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", borderRadius: "inherit" }} />
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          ))}
          <div className="np-masonry-fade-top" />
          <div className="np-masonry-fade-bot" />
        </div>

      </section>

    </div>
  );
};

export default NammaPass;
